import React, { useEffect, useMemo, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import ConfirmModal from "../../common/ConfirmModal";
import { useUserData } from "../../../hooks/useUserData";
import {
  deleteFolderLibraryItem,
  extractFolderLibraryMessage,
  fetchFolderLibrary,
  isFolderLibrarySuccess,
  isFolderLibraryMutationSuccess,
  normalizeFolderCategory,
  normalizeFolderLibraryList,
  saveFolderLibraryItem,
  sortFolderLibraryRowsNewestFirst,
  updateFolderLibraryItem,
} from "./isfFolderLibraryService";
import {
  canAddFolderLibrary,
  canDeleteFolderLibrary,
  canEditFolderLibrary,
} from "./isfPermissionUtils";
import "./folderLibrary.scss";

const CATEGORY_META = {
  eISF: {
    label: "eISF",
    accent: "#0d9488",
    description: "Investigator site folder library",
  },
  ePSF: {
    label: "ePSF",
    accent: "#7c3aed",
    description: "Pharmacy site folder library",
  },
};

const FolderPanel = ({
  category,
  rows,
  canAdd,
  canEdit,
  canDelete,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const meta = CATEGORY_META[category] || CATEGORY_META.eISF;
  const normalizedSearch = String(searchTerm || "").trim().toLowerCase();
  const isSearching = Boolean(normalizedSearch);

  const filteredRows = useMemo(() => {
    if (!normalizedSearch) return rows;
    return rows.filter((row) =>
      String(row.FolderName || "").toLowerCase().includes(normalizedSearch),
    );
  }, [rows, normalizedSearch]);

  const countLabel =
    isSearching && rows.length !== filteredRows.length
      ? `${filteredRows.length}/${rows.length}`
      : String(filteredRows.length);

  return (
    <section
      className="folder-library-panel"
      style={{ "--panel-accent": meta.accent }}
    >
      <div className="folder-library-panel__header">
        <div>
          <div className="folder-library-panel__title-row">
            <span className="folder-library-panel__badge">{meta.label}</span>
            <h5 className="folder-library-panel__title mb-0">Folders</h5>
            <span className="folder-library-panel__count">{countLabel}</span>
          </div>
          <p className="folder-library-panel__subtitle mb-0">{meta.description}</p>
        </div>
        {canAdd && (
          <button
            type="button"
            className="btn btn-primary btn-sm folder-library-panel__add"
            onClick={() => onAdd(category)}
          >
            <i className="fas fa-plus me-1" aria-hidden="true" />
            Add
          </button>
        )}
      </div>

      <div className="folder-library-panel__search">
        <div className="folder-library-panel__search-field">
          <i className="fas fa-search folder-library-panel__search-icon" aria-hidden="true" />
          <input
            type="text"
            className="form-control form-control-sm folder-library-panel__search-input"
            placeholder={`Search ${meta.label} folders...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label={`Search ${meta.label} folders`}
          />
          {searchTerm ? (
            <button
              type="button"
              className="btn btn-link btn-sm folder-library-panel__search-clear"
              aria-label="Clear search"
              onClick={() => setSearchTerm("")}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="folder-library-panel__body">
        {filteredRows.length === 0 ? (
          <div className="folder-library-panel__empty">
            {isSearching ? "No folders match your search." : "No folders yet."}
          </div>
        ) : (
          <ul className="folder-library-panel__list">
            {filteredRows.map((row) => (
              <li key={row.FolderId} className="folder-library-panel__item">
                <div className="folder-library-panel__item-main">
                  <span className="folder-library-panel__item-name">
                    {row.FolderName}
                  </span>
                  {row.SystemDateTime && (
                    <span className="folder-library-panel__item-date">
                      {row.SystemDateTime}
                    </span>
                  )}
                </div>
                {(canEdit || canDelete) && (
                  <div className="folder-library-panel__item-actions">
                    {canEdit ? (
                    <button
                      type="button"
                      className="btn btn-link btn-sm folder-library-panel__action"
                      title="Edit"
                      onClick={() => onEdit(row)}
                    >
                      <i className="fa fa-edit" aria-hidden="true" />
                    </button>
                    ) : null}
                    {canDelete ? (
                    <button
                      type="button"
                      className="btn btn-link btn-sm folder-library-panel__action folder-library-panel__action--danger"
                      title="Delete"
                      onClick={() => onDelete(row)}
                    >
                      <i className="fa fa-trash" aria-hidden="true" />
                    </button>
                    ) : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

const FolderLibrary = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [rows, setRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    FolderId: "",
    FolderName: "",
    FolderCategory: "eISF",
  });
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState({ open: false, row: null });

  const canAdd = userData && canAddFolderLibrary(userData);
  const canEdit = userData && canEditFolderLibrary(userData);
  const canDelete = userData && canDeleteFolderLibrary(userData);

  const eisfRows = useMemo(
    () =>
      sortFolderLibraryRowsNewestFirst(
        rows.filter((row) => normalizeFolderCategory(row.FolderCategory) === "eISF"),
      ),
    [rows],
  );

  const epsfRows = useMemo(
    () =>
      sortFolderLibraryRowsNewestFirst(
        rows.filter((row) => normalizeFolderCategory(row.FolderCategory) === "ePSF"),
      ),
    [rows],
  );

  const loadList = async () => {
    if (!userData?.OrganizationId) return;
    try {
      showLoader("Loading folders...");
      const response = await fetchFolderLibrary({ userData });
      setRows(isFolderLibrarySuccess(response) ? normalizeFolderLibraryList(response) : []);
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to load folder library.",
        type: "danger",
      });
      setRows([]);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (userData?.OrganizationId) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId]);

  const openCreate = (category) => {
    setForm({
      FolderId: "",
      FolderName: "",
      FolderCategory: normalizeFolderCategory(category),
    });
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      FolderId: row.FolderId || "",
      FolderName: row.FolderName || "",
      FolderCategory: normalizeFolderCategory(row.FolderCategory),
    });
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const next = {};
    if (!form.FolderName.trim()) next.FolderName = "Folder name is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    const isEdit = !!form.FolderId;
    if ((isEdit && !canEdit) || (!isEdit && !canAdd) || !validate()) return;
    const folderCategory = normalizeFolderCategory(form.FolderCategory);
    try {
      showLoader(isEdit ? "Updating..." : "Saving...");
      const response = isEdit
        ? await updateFolderLibraryItem({
            userData,
            folderId: form.FolderId,
            folderName: form.FolderName.trim(),
            folderCategory,
          })
        : await saveFolderLibraryItem({
            userData,
            folderName: form.FolderName.trim(),
            folderCategory,
          });

      const ok = isFolderLibraryMutationSuccess(response);

      if (ok) {
        showAlert({
          title: "Success",
          message: extractFolderLibraryMessage(response, isEdit ? "Folder updated." : "Folder saved."),
          type: "success",
        });
        setShowModal(false);
        setForm({ FolderId: "", FolderName: "", FolderCategory: "eISF" });
        await loadList();
      } else {
        const message = extractFolderLibraryMessage(response, "Save failed.");
        if (/already exists/i.test(message)) {
          setErrors((prev) => ({ ...prev, FolderName: message }));
        }
        showAlert({ title: "Error", message, type: "danger" });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Save failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteConfirm = async () => {
    const row = confirmModal.row;
    if (!row?.FolderId || !canDelete) return;
    const deletedFolderId = row.FolderId;
    setConfirmModal({ open: false, row: null });

    try {
      showLoader("Deleting...");
      const response = await deleteFolderLibraryItem({
        userData,
        folderId: deletedFolderId,
      });
      const ok = isFolderLibraryMutationSuccess(response);

      if (ok) {
        setRows((prev) => prev.filter((item) => item.FolderId !== deletedFolderId));
        showAlert({ title: "Success", message: "Folder deleted.", type: "success" });
        await loadList();
      } else {
        showAlert({
          title: "Error",
          message: extractFolderLibraryMessage(response, "Delete failed."),
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Delete failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const modalCategory = normalizeFolderCategory(form.FolderCategory);
  const modalMeta = CATEGORY_META[modalCategory] || CATEGORY_META.eISF;

  return (
    <div className="folder-library-page milestone-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Folder Library" },
        ]}
        actions={[{ icon: "fas fa-refresh", label: "Refresh", onClick: loadList }]}
      />

      <div className="folder-library-grid">
        <FolderPanel
          category="eISF"
          rows={eisfRows}
          canAdd={canAdd}
          canEdit={canEdit}
          canDelete={canDelete}
          onAdd={openCreate}
          onEdit={openEdit}
          onDelete={(row) => setConfirmModal({ open: true, row })}
        />
        <FolderPanel
          category="ePSF"
          rows={epsfRows}
          canAdd={canAdd}
          canEdit={canEdit}
          canDelete={canDelete}
          onAdd={openCreate}
          onEdit={openEdit}
          onDelete={(row) => setConfirmModal({ open: true, row })}
        />
      </div>

      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {form.FolderId ? "Edit Folder" : "Add Folder"}
                  <span
                    className="folder-library-modal__badge ms-2"
                    style={{ backgroundColor: modalMeta.accent }}
                  >
                    {modalMeta.label}
                  </span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <div className="modal-body">
                <label className="form-label">
                  Folder name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control form-control-sm"
                  value={form.FolderName}
                  placeholder="e.g. Monitoring"
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, FolderName: e.target.value }))
                  }
                />
                {errors.FolderName && (
                  <small className="text-danger d-block mt-1">{errors.FolderName}</small>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title="Delete folder?"
        message={`Delete "${confirmModal.row?.FolderName || ""}" (${normalizeFolderCategory(confirmModal.row?.FolderCategory)})? This cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="btn-danger"
        onConfirm={handleDeleteConfirm}
        onClose={() => setConfirmModal({ open: false, row: null })}
      />

      <InfoAlert {...alertState} onClose={hideAlert} />
    </div>
  );
};

export default FolderLibrary;
